import { shuffle } from '../rng/rng';
import { cardById, isTreasure, isHazard, isArtifact } from '../deck';
import { ROUNDS, PHASES, ROUND_END_REASONS, HAZARD_LABELS } from '../constants';

// Everything that happens to the temple itself: opening it, turning a card
// over, and closing it again.

const clone = (state) => ({
  ...state,
  meta: { ...state.meta, seq: state.meta.seq + 1 },
});

const logged = (state, text) => ({
  ...state,
  log: [...state.log, { round: state.meta.round, turn: state.meta.turn, text }],
});

export const playersInTemple = (state) => state.players.filter((p) => p.inTemple);

/** p2/p3 in a sentence: "a second spike trap", not "a second spikes". */
export const hazardLabel = (hazard) => HAZARD_LABELS[hazard] || hazard;

/** Treasure still lying on the path, waiting for somebody to leave and take it. */
export const gemsOnPath = (state) => state.path.reduce((sum, card) => sum + card.left, 0);

/**
 * Is there any reason at all to leave right now?
 *
 * p2 gives the shortcut for the first card - "since no one has reason to
 * leave, you may immediately flip the next card" - and this is that rule
 * generalised to its actual condition.
 *
 * BOTH halves are required, and getting this wrong is easy: leaving is not
 * only about sweeping the path, it is about banking what you are already
 * carrying. A treasure that divides evenly leaves nothing on the path, but
 * everybody's pocket just got heavier and the decision matters enormously.
 * The only genuinely dead decision is an empty path AND empty pockets, which
 * is exactly the situation the rulebook's own note describes.
 */
export const decisionMatters = (state) => (
  gemsOnPath(state) > 0
  || state.artifactsOnPath.length > 0
  || playersInTemple(state).some((p) => p.pocket > 0)
);

/**
 * p3: the round is over. Two ways in, and they differ only in whether the
 * people still inside keep what they were carrying.
 */
export const endRound = (state, reason, endingCardId = null) => {
  let next = clone(state);

  if (reason === ROUND_END_REASONS.HAZARD) {
    // p3: "All players still in the Temple give back all the treasures they
    // took during the round". The tent is untouched.
    //
    // `caught` is remembered because otherwise the two ways of being out of
    // the temple are indistinguishable a moment later, and they could not be
    // more different: one player walked home with the loot and the other
    // dropped it and ran. Cleared at the start of the next expedition.
    next.players = next.players.map((p) => (
      p.inTemple
        ? { ...p, inTemple: false, caught: true, pocket: 0, choice: null }
        : { ...p, choice: null }
    ));
    // p3: "This second Hazard card will be removed from the game."
    next.questRemaining = next.questRemaining.filter((id) => id !== endingCardId);
  } else {
    next.players = next.players.map((p) => ({ ...p, choice: null }));
  }

  // p3: "If there were any Artifacts on the path when the round ends, these
  // treasures are lost forever and removed from the game."
  if (next.artifactsOnPath.length) {
    next.artifactsLost = [...next.artifactsLost, ...next.artifactsOnPath];
  }
  // What survives is only what is still face down in the deck.
  next.artifactsInPlay = next.artifactsInPlay.filter((id) => next.deck.includes(id));
  next.artifactsOnPath = [];

  next.meta = {
    ...next.meta,
    roundOver: { reason, round: next.meta.round, hazard: endingCardId ? cardById(endingCardId)?.hazard ?? null : null },
    phase: next.meta.round >= ROUNDS ? PHASES.FINISHED : PHASES.DECIDING,
  };

  next = logged(next, reason === ROUND_END_REASONS.HAZARD
    ? `A second ${hazardLabel(cardById(endingCardId)?.hazard)} - everyone still inside runs for it empty-handed.`
    : 'Everyone is out. The temple closes.');

  return next;
};

/**
 * Turns one card face up (p2, step 2) and applies it.
 *
 * Treasure is divided among the players who are in the temple RIGHT NOW,
 * which by then never includes anyone who left this turn - they were resolved
 * first, and they are safe from whatever this card turns out to be.
 */
export const revealCard = (state) => {
  if (!state.deck.length) {
    // Unreachable with a legal deck: 15 hazards across 5 types means the
    // sixth hazard is always somebody's second, so a round ends by card 21 at
    // the very latest and the deck holds at least 30. Guarded anyway, and
    // kindly - nobody loses a pocket to an engine bug.
    return endRound(state, ROUND_END_REASONS.EVERYONE_LEFT);
  }

  let next = clone(state);
  const [id, ...rest] = next.deck;
  const card = cardById(id);
  next.deck = rest;
  next.meta = { ...next.meta, turn: next.meta.turn + 1 };

  if (isTreasure(card)) {
    const inTemple = playersInTemple(next);
    // p2: "divided among these players, rounded down".
    const share = Math.floor(card.value / inTemple.length);
    const left = card.value - share * inTemple.length;
    next.players = next.players.map((p) => (p.inTemple ? { ...p, pocket: p.pocket + share } : p));
    next.path = [...next.path, { id, left }];
    return logged(next, left
      ? `${card.value} treasure - ${share} each, ${left} left behind.`
      : `${card.value} treasure - ${share} each.`);
  }

  if (isArtifact(card)) {
    // p2: "the Artifact stays on the path."
    next.path = [...next.path, { id, left: 0 }];
    next.artifactsOnPath = [...next.artifactsOnPath, id];
    return logged(next, 'An artifact! It stays on the path until somebody leaves alone.');
  }

  // Hazard.
  next.path = [...next.path, { id, left: 0 }];
  const seen = next.hazardsSeen[card.hazard] || 0;
  if (seen >= 1) return endRound(next, ROUND_END_REASONS.HAZARD, id);

  next.hazardsSeen = { ...next.hazardsSeen, [card.hazard]: seen + 1 };
  return logged(next, `A ${hazardLabel(card.hazard)} - a glimpse of danger and doom.`);
};

/**
 * Reveals, and keeps revealing while nobody could possibly want to stop.
 *
 * This is the rulebook's own p2 shortcut ("you may immediately flip the next
 * card"), and it terminates because every pass consumes a card.
 */
export const revealUntilDecisionMatters = (state) => {
  let next = revealCard(state);
  while (!next.meta.roundOver && !decisionMatters(next)) {
    next = revealCard(next);
  }
  return next;
};

/**
 * Opens the next temple (p1/p2): reveal that round's artifact, shuffle it into
 * whatever quest cards are left, and turn the first card over.
 */
export const startRound = (state) => {
  let next = clone(state);
  const round = next.meta.round + 1;

  const newArtifact = next.artifactOrder[round - 1];
  const artifactsInPlay = newArtifact ? [...next.artifactsInPlay, newArtifact] : next.artifactsInPlay;

  const [deck, rngState] = shuffle([...next.questRemaining, ...artifactsInPlay], next.rngState);

  next.meta = { ...next.meta, round, turn: 0, roundOver: null, phase: PHASES.DECIDING };
  next.artifactsInPlay = artifactsInPlay;
  next.deck = deck;
  next.rngState = rngState;
  next.path = [];
  next.hazardsSeen = {};
  next.artifactsOnPath = [];
  next.players = next.players.map((p) => ({
    ...p, inTemple: true, caught: false, pocket: 0, choice: null,
  }));

  next = logged(next, `Expedition ${round} sets off.`);
  // p2 note: nobody has anything to lose before the first card, so the
  // pointless first decision is skipped.
  return revealUntilDecisionMatters(next);
};

export const hazardIsDeadly = (state, card) => isHazard(card) && (state.hazardsSeen[card.hazard] || 0) >= 1;
